import {
  INVALID_GETTER_PATH,
  getterIOPort,
  getterPathRoot,
  getterSpaceID,
  getterVariableField,
  getterVariableID,
  isKnownGetterRoot,
  scanGetterCalls,
} from '@sb/remote-control/util/expressions/getterCalls';
import { stepOutputNames } from '@sb/remote-control/util/stepOutputs';
import { VIRTUAL_CAPTURE_POSE_ID_PREFIX } from '@sb/remote-control/util/virtualSpaceItems';
import { ANY_INPUT_PORT_IDS, ANY_OUTPUT_PORT_IDS } from '@sb/routine-runner';

import {
  type Diagnostic,
  type RoutineView,
  type RuleEvaluationContext,
  type ValidationRule,
} from '../../types';
import { isMissingVariable } from '../../util/variables';

const RULE_ID = 'expression.invalidReference';

// The IO ports a control box has, as the runtime keys `kinematicState.ioState`
// and the editor names them. The set is fixed by hardware, not configuration,
// so no context is needed to check a port name.
const IO_PORTS: ReadonlySet<string> = new Set<string>([
  ...ANY_INPUT_PORT_IDS,
  ...ANY_OUTPUT_PORT_IDS,
]);

type FoundExpression = { expression: string; fieldId: string };

function tryParseJson(value: string): unknown {
  const trimmed = value.trim();
  if (trimmed[0] !== '{' && trimmed[0] !== '[') return undefined;

  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

// Conditions are stored as JSON text, so strings that parse as JSON are walked
// too.
function collectJavaScriptExpressions(
  node: unknown,
  fieldId: string,
  out: FoundExpression[],
): void {
  if (node == null) return;

  if (typeof node === 'string') {
    const parsed = tryParseJson(node);
    if (parsed != null) collectJavaScriptExpressions(parsed, fieldId, out);

    return;
  }

  if (typeof node !== 'object') return;

  const record = node as Record<string, unknown>;

  if (record.kind === 'JavaScript' && typeof record.expression === 'string') {
    out.push({ expression: record.expression, fieldId });

    return;
  }

  for (const value of Object.values(record)) {
    collectJavaScriptExpressions(value, fieldId, out);
  }
}

function describeProblem(
  path: string,
  label: string,
  routine: RoutineView,
  ctx: RuleEvaluationContext,
): string | null {
  if (path === INVALID_GETTER_PATH) {
    return `Expression uses "${label}", which is not a valid variable. Choose the variable again.`;
  }

  if (!isKnownGetterRoot(path)) {
    return `Expression reads "${path}", which does not exist while the routine runs ("${getterPathRoot(path)}" is not a variable source).`;
  }

  const ioPort = getterIOPort(path);

  if (ioPort != null && !IO_PORTS.has(ioPort)) {
    return `Expression reads IO port "${ioPort}", which this control box does not have. Ports are named like "Input 1" or "Output 1".`;
  }

  const variableID = getterVariableID(path);

  if (variableID != null) {
    const field = getterVariableField(path);
    const step = routine.stepConfigurations[variableID];

    // Check the runner's output fields, including those hidden in the editor's menu.
    if (step != null && field != null) {
      const outputs = stepOutputNames(step.stepKind, step.args);

      if (!outputs.includes(field)) {
        const produces =
          outputs.length === 0
            ? 'produces no outputs'
            : `produces ${outputs.map((name) => `"${name}"`).join(', ')}`;

        return `Expression reads "${field}" from step "${label}", but a ${step.stepKind} step ${produces}.`;
      }
    } else if (
      field != null &&
      field !== 'value' &&
      routine.environmentVariables.some((v) => v.id === variableID)
    ) {
      return `Expression reads "${field}" from variable "${label}"; a variable is read through "value".`;
    }

    if (step == null && isMissingVariable(variableID, routine, ctx)) {
      return `Expression uses "${label}", which refers to a variable or step that is no longer in this routine.`;
    }
  }

  const spaceID = getterSpaceID(path);

  if (
    spaceID != null &&
    !spaceID.startsWith(VIRTUAL_CAPTURE_POSE_ID_PREFIX) &&
    !routine.space.some((item) => item.id === spaceID) &&
    ctx.globalSpace != null &&
    !ctx.globalSpace.some((item) => item.id === spaceID)
  ) {
    return `Expression uses "${label}", which refers to a space that is no longer in this routine.`;
  }

  return null;
}

/**
 * Rule: every `__get` in a step's JavaScript expressions must read something
 * that exists at runtime. `__get` resolves an unknown path to `undefined`
 * without an error, so a comparison against it quietly never matches.
 */
const expressionReferences: ValidationRule = {
  id: RULE_ID,
  appliesTo: 'step',
  run({ routine, ctx, stepId }) {
    if (!stepId) return [];

    const config = routine.stepConfigurations[stepId];
    if (!config?.args) return [];

    const found: FoundExpression[] = [];

    for (const [field, value] of Object.entries(
      config.args as Record<string, unknown>,
    )) {
      collectJavaScriptExpressions(value, field, found);
    }

    const diagnostics: Diagnostic[] = [];
    const seen = new Set<string>();

    const report = (message: string, fieldId: string) => {
      if (seen.has(message)) return;
      seen.add(message);

      diagnostics.push({
        code: RULE_ID,
        message,
        severity: 'error',
        stepId,
        fieldId,
        sourceRule: RULE_ID,
      });
    };

    for (const { expression, fieldId } of found) {
      const { calls, malformed } = scanGetterCalls(expression);

      for (const bad of malformed) {
        report(
          `Expression contains "${bad.text}", which is not a variable reference. Choose the variable again.`,
          fieldId,
        );
      }

      for (const call of calls) {
        const label = call.label ?? call.path;

        const problem =
          describeProblem(call.path, label, routine, ctx) ??
          (call.dynamicKey
            ? describeProblem(call.dynamicKey, label, routine, ctx)
            : null);

        if (problem) report(problem, fieldId);
      }
    }

    return diagnostics;
  },
};

export const ExpressionReferencesValidation = expressionReferences;
